import DateTimeBase from "./date_time_base_controller";
// Connects to data-controller="date-input"
export default class extends DateTimeBase {
  static targets = ["dateInput"];
  static values = {
    range: String,
    startDate: Boolean,
    endDate: Boolean,
    singleDate: Boolean,
    minDate: String,
    type: String,
    stopScroll: Boolean,
    // Cap a multi-day event's span: the end date can be at most this many
    // calendar days after the start (inclusive). 0 = no cap.
    maxSpanDays: Number
  };

  connect() {
    const modal = this.dateInputTarget.closest('.modal');
    const config = this.buildFlatpickrConfig();
    
    // Only apply scroll prevention if stopScrollValue is true
    if (this.stopScrollValue) this.stopAutoScroll(config, modal)
    
    flatpickr(this.dateInputTarget, config);
  }

  stopAutoScroll(config, modal) {
    const originalScrollIntoView = this.dateInputTarget.scrollIntoView;
    this.dateInputTarget.scrollIntoView = function() {};

    config.appendTo = modal || document.body;

    config.onReady = (selectedDates, dateStr, instance) => {
      if (instance.calendarContainer) {
        instance.calendarContainer.scrollIntoView = function() {};
      }
      if (instance.altInput) {
        instance.altInput.scrollIntoView = function() {};
      }
    };

    config.onOpen = (selectedDates, dateStr, instance) => {
      if (instance.calendarContainer) {
        const input = instance.altInput || this.dateInputTarget;
        const inputRect = input.getBoundingClientRect();
        const calendar = instance.calendarContainer;

        calendar.style.position = 'fixed';
        calendar.style.top = `${inputRect.bottom + 2}px`;
        calendar.style.left = `${inputRect.left}px`;
        calendar.style.zIndex = '9999';
      }
    };

    this.cleanup = () => {
      this.dateInputTarget.scrollIntoView = originalScrollIntoView;
    };
  }
  
  disconnect() {
    if (this.cleanup) {
      this.cleanup();
    }
  }
  
  buildFlatpickrConfig() {
    const config = {
      altInput: true,
      altFormat: "m-d-Y",
      dateFormat: "Y-m-d",
      onChange: this.handleDateChange.bind(this),
    };

    // Add range configuration if present
    if (this.rangeValue != '') {
      config.enable = this.rangeValue.split(",").map((date) => date.trim());
    }

    // Add min date if present, otherwise leave calendar fully open
    if (this.minDateValue != '') {
      config.minDate = this.minDateValue;
    }

    return config;
  }

  handleDateChange(selectedDates, dateStr) {
    if (this.startDateValue) {
      this.updateEndDateMin(dateStr);
    } else if (this.endDateValue) {
      this.validateEndDate(dateStr);
    }
  }

  updateEndDateMin(startDate) {
    const endDateInput = this.getEndDateInput(this.typeValue);
    if (endDateInput) {
      const endFlatpickrInstance = endDateInput.dateInputTarget._flatpickr;
      if (endFlatpickrInstance) {
        endFlatpickrInstance.set("minDate", startDate);

        // Cap the span: end date at most (maxSpanDays - 1) days after the start,
        // so the calendar itself can't select a too-long multi-day range.
        const cap = endDateInput.maxSpanDaysValue;
        if (cap && cap > 0 && startDate) {
          const max = new Date(startDate);
          max.setDate(max.getDate() + (cap - 1));
          endFlatpickrInstance.set("maxDate", max);
          const endVal = endDateInput.dateInputTarget.value;
          if (endVal && new Date(endVal) > max) {
            endDateInput.dateInputTarget.value = "";
          }
        }

        // Clear invalid end date
        const endDate = endDateInput.dateInputTarget.value;
        this.updateEndTime(startDate, endDate);
        if (endDate && new Date(startDate) > new Date(endDate)) {
          endDateInput.dateInputTarget.value = "";
        }
      }
    }
  }

  validateEndDate(endDate) {
    const startDateInput = this.getStartDateInput(this.typeValue);
    if (startDateInput) {
      const startDate = startDateInput.dateInputTarget.value;
      this.updateEndTime(startDate, endDate);
      if (startDate && new Date(endDate) < new Date(startDate)) {
        this.dateInputTarget.value = "";
      }
    }
  }

  updateEndTime(startDate, endDate) {
    if (startDate && endDate) {
      if (new Date(endDate) > new Date(startDate)) {
        const endFlatpickrInstance =
          this.getEndTimeInput(this.typeValue).timeInputTarget._flatpickr;
        if (endFlatpickrInstance) {
          endFlatpickrInstance.set("minTime", null);
        }
      } else {
        const startTime = this.getStartTimeInput(this.typeValue).timeInputTarget.value;
        if (startTime) {
          this.getEndTimeInput(this.typeValue).updateEndTimeMin(startTime);
        }
      }
    }
  }
}
